import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import {
  ISupportTicket,
  ISupportTicketCounts,
  ISupportTicketPage,
  ISupportTicketReply,
  ISupportTicketUpdate,
  ISupportTicketWhere,
} from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'admin/support-ticket';

/**
 * Customer service tickets (admin-only). The contract lives in objects'
 * `interfaces/support-ticket.ts`; until the server has these endpoints they
 * answer 404, which `ticketsNotLive()` recognises so the screens can say so.
 */
@Injectable({ providedIn: 'root' })
export class AdminSupportTicketApiService {
  private readonly http = inject(HttpClient);

  list(where: ISupportTicketWhere = {}): Observable<ISupportTicketPage> {
    return this.http.get<ISupportTicketPage>(`${API_URL}?${objectToUrlParams(where, true)}`);
  }

  /** One ticket with its messages, oldest first. */
  fetch(id: number): Observable<ISupportTicket> {
    return this.http.get<ISupportTicket>(`${API_URL}/${id}`);
  }

  update(id: number, changes: ISupportTicketUpdate): Observable<ISupportTicket> {
    return this.http.patch<ISupportTicket>(`${API_URL}/${id}`, changes);
  }

  /** Emails the requester and adds the reply to the ticket; returns the updated ticket. */
  reply(id: number, reply: ISupportTicketReply): Observable<ISupportTicket> {
    return this.http.post<ISupportTicket>(`${API_URL}/${id}/reply`, reply);
  }

  counts(): Observable<ISupportTicketCounts> {
    return this.http.get<ISupportTicketCounts>(`${API_URL}/counts`);
  }
}

/** The ticket endpoints aren't on this server yet (404), as opposed to failing. */
export function ticketsNotLive(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 404;
}
